'use client';

import { useEffect, useState } from 'react';

interface Credential {
	email: string;
	password: string;
	role: string;
	permissions: string;
}

interface DemoCredentialsProps {
	onFill: (email: string, password: string) => void;
	onSubmit: () => void;
}

const DEMO_CREDENTIALS: Credential[] = [
	{
		email: 'allsuperadmin@fdsa.com',
		password: 'fdsafdsa',
		role: 'All Workspaces Superadmin',
		permissions:
			'Superadmin of all workspaces (can create/delete agents, configure billing), agent superadmin everywhere',
	},
	{
		email: 'allworkspacemember@fdsa.com',
		password: 'fdsafdsa',
		role: 'All Workspace Member',
		permissions: 'Member of all workspaces, agent member everywhere',
	},
	{
		email: 'allworkspaceadmin@fdsa.com',
		password: 'fdsafdsa',
		role: 'All Workspace + Agent Admin',
		permissions: 'Member of all workspaces, agent admin everywhere',
	},
	{
		email: 'agentsuperadmin@fdsa.com',
		password: 'fdsafdsa',
		role: 'Agent Superadmin',
		permissions: 'Member everywhere, agent superadmin on Agent 1',
	},
	{
		email: 'owner@fdsa.com',
		password: 'fdsafdsa',
		role: 'MetaHealth Owner',
		permissions: 'Owner of MetaHealth, member of Wellness, superadmin on Meta agents',
	},
	{
		email: 'metaadmin@fdsa.com',
		password: 'fdsafdsa',
		role: 'MetaHealth Admin',
		permissions: 'Member of MetaHealth only, agent admin on Agents 1 & 2',
	},
	{
		email: 'wellnessadmin@fdsa.com',
		password: 'fdsafdsa',
		role: 'Wellness Admin',
		permissions: 'Member of Wellness only, agent admin on Agent 3',
	},
	{
		email: 'dualmember@fdsa.com',
		password: 'fdsafdsa',
		role: 'Dual Member',
		permissions: 'Member of both workspaces, agent member on all agents',
	},
	{
		email: 'wellnessmember@fdsa.com',
		password: 'fdsafdsa',
		role: 'Wellness Member',
		permissions: 'Member of Wellness only, agent member on Agent 3',
	},
	{
		email: 'metamember@fdsa.com',
		password: 'fdsafdsa',
		role: 'MetaHealth Member',
		permissions: 'Member of MetaHealth only, agent member on Agents 1 & 2',
	},
	{
		email: 'test@test.com',
		password: 'TestPassword123!',
		role: 'Test User',
		permissions: 'Member of MetaHealth only, agent member on Agents 1 & 2',
	},
];

/** RBAC test accounts on Inbound Leads (Code) in MetaHealth; seeded by src/cli/seedRbacTest.ts. */
const RBAC_TEST_CREDENTIALS: Credential[] = [
	{
		email: 'views-north-only@fdsa.com',
		role: 'North users',
		permissions: 'Sees, edits and exports only users in the North view; runs Update User Fields',
	},
	{
		email: 'views-north-readonly@fdsa.com',
		role: 'North read-only',
		permissions: "Sees North only; can rate and discuss AI replies; can't edit fields.",
	},
	{
		email: 'views-north-creator@fdsa.com',
		role: 'North view creator',
		permissions: 'Sees North and can create child views under it',
	},
	{
		email: 'views-north-no-export@fdsa.com',
		role: 'North no export',
		permissions: 'Sees and edits North, cannot download',
	},
	{
		email: 'views-all-readonly@fdsa.com',
		role: 'All views read-only',
		permissions: 'Sees every view, cannot change anything',
	},
	{
		email: 'drafts-prompts-knowledge-only@fdsa.com',
		role: 'Content editor',
		permissions: 'Edits drafts only in prompts/ and knowledge/, cannot publish',
	},
	{
		email: 'workspace-billing-only@fdsa.com',
		role: 'Billing manager',
		permissions: 'Workspace billing only',
	},
	{
		email: 'workspace-members-manager@fdsa.com',
		role: 'Member manager',
		permissions: 'Invites members and changes roles, no agent editing',
	},
	{
		email: 'agent-admin-one-agent@fdsa.com',
		role: 'Agent admin, one agent',
		permissions: 'Admin of Inbound Leads (Code) only; no billing or member permissions',
	},
].map((c) => ({ ...c, password: 'fdsafdsa' }));

export default function DemoCredentials({ onFill, onSubmit }: DemoCredentialsProps) {
	const [isDevMode, setIsDevMode] = useState(false);

	useEffect(() => {
		setIsDevMode(process.env.NODE_ENV === 'development');
	}, []);

	if (!isDevMode) {
		return null;
	}

	const handleQuickFill = (email: string, password: string) => {
		onFill(email, password);
		setTimeout(() => {
			onSubmit();
		}, 0);
	};

	return (
		<div className="mt-8 border-t pt-8">
			<div className="mb-4">
				<p className="text-sm font-semibold text-foreground mb-4">🔧 Development Demo Credentials</p>
				{[
					{ title: null, list: DEMO_CREDENTIALS },
					{ title: 'RBAC tests · Inbound Leads (Code)', list: RBAC_TEST_CREDENTIALS },
				].map(({ title, list }) => (
					<div key={title ?? 'base'} className="grid gap-2 text-xs mb-6">
						{title && <p className="text-xs font-semibold text-muted-foreground mt-2">{title}</p>}
						{list.map((cred) => (
							<div
								key={cred.email}
								className="flex items-center justify-between p-2 bg-muted/50 rounded border border-border hover:bg-muted"
							>
								<div className="flex-1">
									<p className="font-mono text-foreground">{cred.email}</p>
									<p className="text-foreground font-medium">{cred.role}</p>
									<p className="text-muted-foreground text-[11px] mt-0.5">
										{cred.permissions}
									</p>
								</div>
								<button
									type="button"
									onClick={() => handleQuickFill(cred.email, cred.password)}
									className="ml-2 px-3 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors whitespace-nowrap"
								>
									Fill & Login
								</button>
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	);
}
